import type { FC } from "react";
import type { DistanceToLayerResponse, GenerationPowerSummaryResponse } from "../../services/api";
import { INFRASTRUCTURE_INSIDE_SELECTION_THRESHOLD_KM } from "../ReportComponent/constants/reportConstants";

type SystemMode = "sin" | "distribuidora";
type EnergyMode = "solar" | "eolico" | "both";

const ENERGY_TITLES: Record<EnergyMode, string> = {
  solar: "Potencial Solar",
  eolico: "Potencial Eólico",
  both: "Potencial Solar/Eólico",
};

function formatDistance(distanceKm: number): string {
  if (distanceKm < INFRASTRUCTURE_INSIDE_SELECTION_THRESHOLD_KM) return "Dentro da área selecionada";
  return `${distanceKm.toFixed(2)} km`;
}

function getLineName(props: Record<string, unknown>, mode: SystemMode): string {
  if (mode === "distribuidora") {
    return String(props.nome || props.codigo || props.orig_dest || "Linha de Distribuição");
  }
  return String(props.nomelongo || props.nomerede || "LT em Operação");
}

function getSubstationName(props: Record<string, unknown>, mode: SystemMode): string {
  const fallback = mode === "distribuidora" ? "Unidade de Transformação" : "Subestação";
  return String(props.cod_id || props.nome || props.nomelongo || fallback);
}

interface DistanceInfoProps {
  distanceData: {
    existing?: DistanceToLayerResponse;
    planned?: DistanceToLayerResponse;
    substation_existing?: DistanceToLayerResponse;
  };
  drawingName?: string;
  energyMode?: EnergyMode;
  systemMode?: SystemMode;
  isLoading?: boolean;
  generationPowerSummary?: GenerationPowerSummaryResponse | null;
  showEnergyTitle?: boolean;
}

export const DistanceInfo: FC<DistanceInfoProps> = ({
  distanceData,
  drawingName,
  energyMode = "solar",
  systemMode = "sin",
  isLoading = false,
  generationPowerSummary,
  showEnergyTitle = true,
}) => {
  const nearest = distanceData.existing?.nearest_feature;
  const plannedFeature = distanceData.planned?.nearest_feature;
  const substationFeature = distanceData.substation_existing?.nearest_feature;
  const title = ENERGY_TITLES[energyMode];
  const lineLabel = systemMode === "distribuidora" ? "Linha de Distribuição mais próxima" : "Linha de Transmissão mais próxima";
  if (isLoading) {
    return (
      <div className="flex flex-col items-start gap-2 self-stretch">
        {showEnergyTitle && <div className="font-medium text-[16px] leading-5 text-[#1C1D20]">{title}</div>}
        <div className="font-normal text-[16px] leading-5 text-[#0C2856]">{drawingName}</div>
        <div className="w-full rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 flex flex-col gap-2 animate-pulse">
          <div className="h-3 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
        </div>
        <div className="w-full rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 flex flex-col gap-2 animate-pulse">
          <div className="h-3 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (!nearest && !plannedFeature && !substationFeature) {
    return (
      <div className="flex flex-col items-start gap-2 self-stretch">
        {showEnergyTitle && <div className="font-medium text-[16px] leading-5 text-[#1C1D20]">{title}</div>}
        <div className="font-normal text-[16px] leading-5 text-[#0C2856]">{drawingName}</div>
        <div className="w-full rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-[12px] text-gray-500 leading-relaxed">
          Nenhuma infraestrutura elétrica identificada nas proximidades desta área.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-2 self-stretch">
      {showEnergyTitle && <div className="font-medium text-[16px] leading-5 text-[#1C1D20]">{title}</div>}
      <div className="font-normal text-[16px] leading-5 text-[#0C2856]">{drawingName}</div>

      {generationPowerSummary && generationPowerSummary.sources.length > 0 && (
        <div className="w-full rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-bold text-[#0C2856]">
              Fontes de Geração na Área
            </span>
            <span className="text-[12px] font-bold text-[#0C2856]">
              Total: {generationPowerSummary.grand_total_mw.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MW
            </span>
          </div>
          <div className="flex flex-col gap-1">
            {generationPowerSummary.sources.map((s) => (
              <div key={s.layer_name} className="flex items-center justify-between text-[11px]">
                <span className="text-gray-600">
                  {s.source_type}
                  <span className="text-gray-400 ml-1">({s.count} {s.count === 1 ? "usina" : "usinas"})</span>
                </span>
                <span className="font-semibold text-gray-800">
                  {s.total_mw.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MW
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {nearest ? (
        <div className="flex flex-col items-start p-2 gap-3 bg-[#F5F8FF] rounded self-stretch">
          <div className="font-normal text-[12px] leading-4 text-[#0C2856]">{lineLabel}</div>
          <div className="flex flex-row justify-between items-end gap-4 self-stretch">
            <div className="font-normal text-[12px] leading-4 text-[#1C1D20]">
              {getLineName(nearest.properties, systemMode)}
            </div>
            <div className="font-medium text-[14px] leading-[18px] text-[#1C1D20]">
              {formatDistance(nearest.distance_km)}
            </div>
          </div>
          {systemMode === "distribuidora" && nearest.properties.tensao_kv != null && (
            <div className="font-normal text-[11px] leading-4 text-[#6b7280]">
              Tensão: {nearest.properties.tensao_kv} kV
            </div>
          )}
        </div>
      ) : (
        <div className="w-full rounded bg-amber-50 border border-amber-100 px-3 py-2 text-[11px] text-amber-700 leading-relaxed">
          {lineLabel.replace("mais próxima", "")} não identificada nesta região.
        </div>
      )}

      {plannedFeature && (
        <div className="flex flex-col items-start p-2 gap-3 bg-[#F5F8FF] rounded self-stretch">
          <div className="font-normal text-[12px] leading-4 text-[#0C2856]">LT planejada mais próxima</div>
          <div className="flex flex-row justify-between items-end gap-4 self-stretch">
            <div className="font-normal text-[12px] leading-4 text-[#1C1D20]">
              {getLineName(plannedFeature.properties, systemMode)}
            </div>
            <div className="font-medium text-[14px] leading-[18px] text-[#1C1D20]">
              {formatDistance(plannedFeature.distance_km)}
            </div>
          </div>
        </div>
      )}

      {substationFeature && (
        <div className="flex flex-col items-start p-2 gap-3 bg-[#F5F8FF] rounded self-stretch">
          <div className="font-normal text-[12px] leading-4 text-[#0C2856]">Subestação mais próxima</div>
          <div className="flex flex-row justify-between items-end gap-4 self-stretch">
            <div className="font-normal text-[12px] leading-4 text-[#1C1D20]">
              {getSubstationName(substationFeature.properties, systemMode)}
            </div>
            <div className="font-medium text-[14px] leading-[18px] text-[#1C1D20]">
              {formatDistance(substationFeature.distance_km)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
